import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import _ from 'lodash';
import { useMutation, useQuery } from 'react-query';
import { toastr } from 'react-redux-toastr';
import {
  Modal,
  ModalConfirm,
  useConfirmModal,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Box,
  Button,
  SubmitButton,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { RefreshIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { formatCurrency } from '@fortress-technology-solutions/fortress-component-library/utils';
import IntacctModal from './IntacctModal';
import messages from '../messages';
import useIntl from '../../../hooks/useIntl';
import { useAutoCalculations, ignoreDifferenceFields } from './calculations';
import Part1, { data as part1Data } from './part1';
import Part2, { data as part2Data } from './part2';
import Part3, { data as part3Data } from './part3';
import Part5, { data as part5Data } from './part5';
import Comments from './comments';
import Review from './review';
import StatusCard from './statusCard';
import { useForm } from 'react-hook-form';
import RdBudgetFormService from '../../../services/rdBudgetFormService';
import useRdBudgetCompliance from '../hooks.useRDBudgetCompliance';
import useRdBudgetFormQuery from '../hooks.useRdBudgetFormQuery';
import { post } from '../../../utils/api';
import { FormattedMessage } from 'react-intl';
import UnitService from '../../../services/unitService';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import { useBudgetTypeModal } from './BudgetTypeModal';
import { COMPLIANCE_STATES, INTACCT_ABSOLUTE_FIELDS } from '../constants';
import { getAffordableFees, getManagementFees } from '../utils';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import type { BudgetType } from './types';

function calculatePartVTotals(rawIntacctData) {
  if (!rawIntacctData) return { reservesSum: 0, operatingSum: 0 };
  // pulled from intacct, we should be calculating these
  // values from the account groups directly
  const includedSectionNames = [
    'Appliances -',
    'Carpet And Vinyl -',
    'Cabinets -',
    'Doors -',
    'Window Covering -',
    'Heating And Air Conditioning -',
    'Plumbing -',
    'Major Electrical -',
    'Structures -',
    'Paving -',
    'Landscape And Grounds -',
    'Accessibility Features -',
    'Automation Equipment -',
    'Other List',
  ];
  const partVData = rawIntacctData['Form RD 3560-7 - Part V'].sort((a, b) =>
    a.accountGroupName.localeCompare(b.accountGroupName),
  );
  // TOTAL CAPITAL EXPENSES (Reserves)
  const reservesSum = partVData.reduce((acc, curr) => {
    const agName = curr.accountGroupName;
    const isIncludedInCalculation = includedSectionNames.some((sectionName) =>
      agName.startsWith(sectionName),
    );
    if (
      isIncludedInCalculation &&
      (agName.includes('(Reserves)') || agName.includes('(Reserve)'))
    ) {
      return acc + parseFloat(curr.currentBudgetItemSum);
    }
    return acc;
  }, 0);
  // TOTAL CAPITAL EXPENSES (Operating)
  const operatingSum = partVData.reduce((acc, curr) => {
    const agName = curr.accountGroupName;
    const isIncludedInCalculation = includedSectionNames.some((sectionName) =>
      agName.startsWith(sectionName),
    );
    if (isIncludedInCalculation && agName.includes('(Operating)')) {
      return acc + parseFloat(curr.currentBudgetItemSum);
    }
    return acc;
  }, 0);

  return {
    reservesSum,
    operatingSum,
  };
}

const RDBudgetModal = (props) => {
  const {
    open,
    closeModal,
    forecastName,
    rentForecast,
    intacctOpen,
    openIntacctModal,
    closeIntacctModal,
    manageRentForecastData,
    vacancyRateActual,
    totalUnits,
    newbasicRentPotentialIncome,
    fpRows,
    changeType,
    hasEditPermission,
  } = props;
  const [isRequestActive, setRequestActive] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const intacctValues = useRef({});
  const [intacctDifferences, setIntacctDifferences] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateDisabled, setGenerateDisabled] = useState(false);
  const [reserveAutoFilled, setReserveAutoFilled] = useState(false);
  const selectedProperty = useSelectedProperty();
  const [justSaved, setJustSaved] = useState(false);
  const [userInteractionHappened, setUserInteractionHappened] = useState(false);
  const userInteractionRef = useRef(false);
  const [isTabRefreshing, setIsTabRefreshing] = useState(false);
  const [isCalculating] = useState(false);
  const ignoreAutoFieldsRef = useRef(new Set());
  const intl = useIntl();

  const [tableData, setTableData] = useState({
    page: 0,
  });
  const [complianceStatus, setComplianceStatus] = useState(null);
  const budgetType: BudgetType = useMemo((): BudgetType => {
    if (rentForecast?.approvedFrom) {
      return 'approved';
    }
    if (!hasEditPermission) {
      return rentForecast?.createdFrom ? 'custom-disabled' : 'disabled';
    }
    if (rentForecast?.createdFrom) {
      return 'custom';
    }

    return 'normal';
  }, [
    rentForecast?.createdFrom,
    rentForecast?.approvedFrom,
    hasEditPermission,
  ]);

  const rentForecastId = rentForecast?.id;
  const rentForecastYear = rentForecast?.year;
  const reserveAccountRequiredBalance =
    manageRentForecastData?.rentForecasts?.affordableSetUp
      ?.reserveAccountRequiredBalance;
  const isApprovedForecast = Boolean(rentForecast?.approvedFrom);
  const isWizardCreated = Boolean(rentForecast?.createdFrom);
  const allowFullEdit = rentForecast?.parameters?.allowFullEdit ?? false;

  const useIntacctDifference = useCallback((fieldName, value, isDisabled) => {
    const intacctFields = Object.keys(intacctValues.current);

    if (
      isDisabled ||
      intacctFields.length === 0 ||
      !_.has(intacctValues.current, fieldName)
    ) {
      return;
    }

    const formValue = parseFloat(value) || 0;
    const intacctValue = parseFloat(intacctValues.current[fieldName]) || 0;
    const isDifferent = Math.abs(formValue - intacctValue) > 0.01;

    setIntacctDifferences((prev) => {
      if (prev[fieldName] === isDifferent) {
        return prev;
      }

      return { ...prev, [fieldName]: isDifferent };
    });
  }, []);

  const triggerFullIntacctDifference = (formValues, intacctData) => {
    const formData = _.omitBy(intacctData, (value, key) => {
      return ignoreDifferenceFields.has(key);
    });

    const differences = Object.entries(formData)
      .map(([key, value]) => {
        const formValue = parseFloat(formValues[key]) || 0;
        const intacctValue = parseFloat(value || 0);

        return [key, Math.abs(formValue - intacctValue) > 0.01];
      })
      .reduce(
        (acc, [key, isDifferent]) => ({ ...acc, [key]: isDifferent }),
        {},
      );

    setIntacctDifferences(differences);
  };

  const {
    data: results,
    refetch: originalRefetch,
    isFetching,
  } = useRdBudgetFormQuery(rentForecastId, {
    enabled: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
    keepPreviousData: false,
  });

  const useFormReturn = useForm({
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    resetField,
    formState: { dirtyFields, errors },
    setValue,
    getValues,
    reset,
    subscribe,
  } = useFormReturn;

  const setAllUserInteraction = useCallback((userInteraction) => {
    userInteractionRef.current = userInteraction;
    setUserInteractionHappened(userInteraction);
  }, []);

  useEffect(() => {
    const handleFormInputChange = (event) => {
      const target = event.target;
      if (!target) return;

      if (userInteractionRef.current) return;
      // Check if the target is a form input/textarea/select
      const isFormInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT';
      if (isFormInput) {
        setAllUserInteraction(true);
      }
    };
    document.addEventListener('input', handleFormInputChange, true);
    return () => {
      document.removeEventListener('input', handleFormInputChange, true);
    };
  }, [setAllUserInteraction]);

  const { recalculate, savedVacancyInputsRef } = useAutoCalculations(
    useFormReturn,
    useIntacctDifference,
  );

  const managementFees = React.useMemo(
    () => ({
      RDNonContinuousParcels:
        selectedProperty.setup.RDNonContinuousParcelsEnabled,
      RDRemoteLocation: selectedProperty.setup.RDRemoteLocationEnabled,
      RDTroubledOrCritical: selectedProperty.setup.RDTroubledOrCritical,
    }),
    [
      selectedProperty.setup.RDNonContinuousParcelsEnabled,
      selectedProperty.setup.RDRemoteLocationEnabled,
      selectedProperty.setup.RDTroubledOrCritical,
    ],
  );
  const queryKey = React.useMemo(
    () => [
      'useUnitsQuery',
      selectedProperty.id,
      selectedProperty.organizationId,
    ],
    [selectedProperty.id, selectedProperty.organizationId],
  );
  const { data: units } = useQuery(
    queryKey,
    async () => {
      const unitService = new UnitService();
      const units = await unitService.getAll(
        selectedProperty.id,
        selectedProperty.organizationId,
        true,
      );
      return units;
    },
    {
      enabled: open && !!selectedProperty.id,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  );

  const onCreate = React.useCallback(
    async (selectedBudgetType) => {
      setGenerateDisabled(true);
      setIsGenerating(true);
      try {
        // Get the current form data with all calculations
        const currentFormData = getValues();

        // Save the form data FIRST before generating the report
        const rdBudgetFormService = new RdBudgetFormService();
        const rdBudgetData = {
          rentForecastId,
          formData: currentFormData,
        };

        // Save form data to backend
        await rdBudgetFormService.upsertRdBudgetForm(rdBudgetData);

        // NOW generate the report with the saved data
        await rdBudgetFormService.generateRdBudgetReport(
          selectedProperty.organizationId,
          selectedProperty.id,
          rentForecastId,
          rentForecast.parameters?.budgetType ?? selectedBudgetType,
          null,
          'BUDGET',
          rentForecast.name,
        );
        toastr.success(
          intl.formatMessage(messages.success),
          intl.formatMessage(messages.reportGenerated),
        );
      } catch (e) {
        console.error(e);
        toastr.error(
          intl.formatMessage(messages.error),
          intl.formatMessage(messages.generateRDReportError),
          { timeOut: 0, showCloseButton: true },
        );
      } finally {
        setGenerateDisabled(false);
        setIsGenerating(false);
        closeBudgetTypeModal();
      }
    },
    [selectedProperty, rentForecastId, intl, useFormReturn, tableData?.page],
  );

  const {
    openBudgetTypeModal,
    budgetTypeModalNode,
    closeModal: closeBudgetTypeModal,
  } = useBudgetTypeModal({
    onGenerate: onCreate,
    generating: isGenerating,
    value: rentForecast?.parameters?.budgetType ?? '',
  });

  const { openConfirm, openConfirmModal, closeConfirmModal } =
    useConfirmModal();

  const getIntacctFormValues = (intacctData) => {
    const part1PageLabel = 'Form RD 3560-7 - Part I';
    const part2PageLabel = 'Form RD 3560-7 - Part II';
    const part3PageLabel = 'Form RD 3560-7 - Part III';
    const part5PageLabel = 'Form RD 3560-7 - Part V';
    const pageDataMap = {
      [part1PageLabel]: part1Data,
      [part2PageLabel]: part2Data,
      [part3PageLabel]: part3Data,
      [part5PageLabel]: part5Data,
    };
    const cleanRegexp = /[^A-Za-z0-9().]/g;
    const formConfigArray = Object.keys(pageDataMap)
      .map((pageName, pageIndex) => {
        const pageConfig = pageDataMap[pageName];
        return pageConfig
          .map((section, sectionIndex) => {
            return section.rows.map((row, rowIndex) => {
              const cleanLabel = row.label
                .toLowerCase()
                .replace(cleanRegexp, '');
              const labelNumber = cleanLabel.match(/\d+\./)?.[0] || null;
              // row.labelSuffix
              const cleanLabelWithSuffix = `${row.label ?? ''} ${
                row.labelSuffix ?? ''
              }`
                .toLowerCase()
                .replace(cleanRegexp, '');
              const cleanLabelWithSection = `${section.header ?? ''} ${
                row.label ?? ''
              }`
                .toLowerCase()
                .replace(cleanRegexp, '');
              const cleanLabelWithSectionAndSuffix = `${section.header ?? ''} ${
                row.label ?? ''
              } ${row.labelSuffix ?? ''}`
                .toLowerCase()
                .replace(cleanRegexp, '');
              const cleanLabelWithoutNumber = cleanLabel.replace(
                labelNumber,
                '',
              );
              return {
                ...row,
                pageName,
                pageIndex,
                sectionName: section.header,
                sectionIndex,
                rowIndex,
                cleanLabel,
                labelNumber,
                cleanLabelWithSuffix,
                cleanLabelWithSection,
                cleanLabelWithSectionAndSuffix,
                cleanLabelWithoutNumber,
              };
            });
          })
          .flat();
      })
      .flat();

    const resultArray = Object.keys(intacctData)
      .map((pageLabel) => {
        const pageData = intacctData[pageLabel];
        return pageData.map((page) => {
          let accountGroupName = page.accountGroupName.trim();
          let part5Field = null;
          let originalAccountGroupName = accountGroupName;
          if (pageLabel === part5PageLabel) {
            // Operating or Reserve
            part5Field =
              /^.*\((Operating|Reserve|Reserves)\)$/g.exec(
                accountGroupName,
              )?.[1] || null;
            accountGroupName = accountGroupName.replace(`(${part5Field})`, '');
          }
          const labelNumber = accountGroupName.match(/\d+\./)?.[0] || null;
          const cleanAccountGroupName = accountGroupName
            .toLowerCase()
            .replace(cleanRegexp, '');
          return {
            pageLabel,
            ...page,
            cleanAccountGroupName,
            labelNumber,
            part5Field,
            originalAccountGroupName,
          };
        });
      })
      .flat();

    const part1ResultsArray = resultArray.filter(
      (page) => page.pageLabel === part1PageLabel,
    );
    const part2ResultsArray = resultArray.filter(
      (page) => page.pageLabel === part2PageLabel,
    );
    const part3ResultsArray = resultArray.filter(
      (page) => page.pageLabel === part3PageLabel,
    );
    const part5ResultsArray = resultArray.filter(
      (page) => page.pageLabel === part5PageLabel,
    );

    const enhancedResultsByPageLabel = {
      [part1PageLabel]: part1ResultsArray,
      [part2PageLabel]: part2ResultsArray,
      [part3PageLabel]: part3ResultsArray,
      [part5PageLabel]: part5ResultsArray,
    };

    const allMatchData = formConfigArray.map((formConfig) => {
      // check labelNumber, label, label with suffix, and leven distance
      const enhancedPageResults =
        enhancedResultsByPageLabel[formConfig.pageName];
      const intacctDataMatches = enhancedPageResults
        .map((pageData) => {
          const labelNumberMatch =
            formConfig.labelNumber &&
            pageData.labelNumber &&
            formConfig.labelNumber === pageData.labelNumber;
          const labelMatchesAGName =
            formConfig.cleanLabel === pageData.cleanAccountGroupName;
          const labelWithSuffixMatchesAGName =
            formConfig.cleanLabelWithSuffix === pageData.cleanAccountGroupName;

          const cleanLabelWithSectionMatchesAGName =
            formConfig.cleanLabelWithSection === pageData.cleanAccountGroupName;
          const cleanLabelWithSectionAndSuffixMatchesAGName =
            formConfig.cleanLabelWithSectionAndSuffix ===
            pageData.cleanAccountGroupName;

          const cleanLabelWithoutNumberMatchesAGName =
            formConfig.cleanLabelWithoutNumber ===
            pageData.cleanAccountGroupName;

          const numMatchingConditions = [
            labelNumberMatch,
            labelMatchesAGName,
            labelWithSuffixMatchesAGName,
            cleanLabelWithSectionMatchesAGName,
            cleanLabelWithSectionAndSuffixMatchesAGName,
            cleanLabelWithoutNumberMatchesAGName,
          ].filter((condition) => condition).length;

          const matchData = {
            labelNumberMatch,
            labelMatchesAGName,
            labelWithSuffixMatchesAGName,
            cleanLabelWithSectionMatchesAGName,
            cleanLabelWithSectionAndSuffixMatchesAGName,
            cleanLabelWithoutNumberMatchesAGName,
            numMatchingConditions,
            intacctData: pageData,
            formConfig,
          };
          return matchData;
        })
        .filter((rowMatch) => {
          return rowMatch.numMatchingConditions > 0;
        })
        // Other section is too ambiguous, leave it last
        .sort(
          (a, b) =>
            (a.intacctData.accountGroup?.NAME ?? '').length -
            (b.intacctData.accountGroup?.NAME ?? '').length,
        );

      return {
        ...formConfig,
        intacctDataMatches,
      };
    });
    console.log('allMatchData', allMatchData);

    const fieldsWithNoMatches = allMatchData.filter((matchData) => {
      return matchData.intacctDataMatches.length === 0;
    });
    console.log('fieldsWithNoMatches', fieldsWithNoMatches);

    const fieldsWithMultipleMatches = allMatchData.filter((matchData) => {
      return matchData.intacctDataMatches.length > 1;
    });
    console.log('fieldsWithMultipleMatches', fieldsWithMultipleMatches);

    const fieldsWithMatches = allMatchData.filter((matchData) => {
      return matchData.intacctDataMatches.length > 0;
    });
    console.log('fieldsWithMatches', fieldsWithMatches);

    const newFormValues = {};
    fieldsWithMatches.forEach((matchData) => {
      if (matchData.pageIndex === 3) {
        // Part 5 uses different field names
        // reserveProposed, reserveActual, operatingProposed, operatingActual
        matchData.intacctDataMatches.forEach((match) => {
          const { formConfig, intacctData } = match;

          // Skip values that are already matched
          if (intacctData.matched) {
            return;
          }

          const keyParts = [
            formConfig.pageIndex,
            formConfig.sectionIndex,
            formConfig.rowIndex,
          ];

          let part5Field;

          if (intacctData.part5Field) {
            part5Field = intacctData.part5Field.toLowerCase().replace(/s$/, '');
          }

          const actualName = [...keyParts, `${part5Field}Actual`].join('-');

          newFormValues[actualName] = intacctData.actualGlEntriesSum;

          intacctData.matched = true;
        });
        return;
      }

      // find the one with the most matches
      const match = matchData.intacctDataMatches.sort(
        (a, b) => b.numMatchingConditions - a.numMatchingConditions,
      )[0];
      const { formConfig, intacctData } = match;
      const keyParts = [
        formConfig.pageIndex,
        formConfig.sectionIndex,
        formConfig.rowIndex,
      ];
      const currentName = [...keyParts, 'currentBudget'].join('-');
      const actualName = [...keyParts, 'actual'].join('-');

      // Some fields must have their values processed as absolute
      newFormValues[currentName] = INTACCT_ABSOLUTE_FIELDS.includes(currentName)
        ? Math.abs(intacctData.currentBudgetItemSum)
        : intacctData.currentBudgetItemSum;
      newFormValues[actualName] = INTACCT_ABSOLUTE_FIELDS.includes(actualName)
        ? Math.abs(intacctData.actualGlEntriesSum)
        : intacctData.actualGlEntriesSum;
    });

    const { reservesSum, operatingSum } = calculatePartVTotals(intacctData);
    const reserveFieldName = '2-0-4-currentBudget';
    const operatingFieldName = '1-0-8-currentBudget';
    newFormValues[reserveFieldName] = reservesSum;
    newFormValues[operatingFieldName] = operatingSum;

    return newFormValues;
  };

  const handleStatusCardClick = useCallback(
    async (index) => {
      const previousPage = tableData.page;
      if (previousPage === index) return;

      setIsTabRefreshing(true);
      const startTime = Date.now();

      try {
        setTableData((prev) => ({ ...prev, page: index }));
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Ensure spinner shows for at least 400ms
        const elapsed = Date.now() - startTime;
        const MIN_LOADING_DURATION = 400;
        if (elapsed < MIN_LOADING_DURATION) {
          await new Promise((resolve) => setTimeout(resolve, 400 - elapsed));
        }
      } finally {
        setIsTabRefreshing(false);
      }
    },
    [tableData.page, useFormReturn],
  );
  const [formStabilized, setFormStabilized] = useState(false);

  const handleCloseWithoutSave = () => {
    if (isRequestActive) {
      return;
    }

    if (
      justSaved ||
      !formStabilized ||
      !userInteractionHappened ||
      rentForecast?.approvedFrom
    ) {
      closeModal();
      return;
    }

    openConfirmModal();
  };

  const resolveWorstComplianceStatus = (rdBudgetCompliance) => {
    let hasWarning = false;

    for (let part of rdBudgetCompliance) {
      for (let item of part.items) {
        if (item.state === COMPLIANCE_STATES.fail) {
          return COMPLIANCE_STATES.fail;
        }

        if (item.state === COMPLIANCE_STATES.warning) {
          hasWarning = true;
        }
      }
    }

    return hasWarning ? COMPLIANCE_STATES.warning : COMPLIANCE_STATES.pass;
  };

  useEffect(() => {
    if (open && results?.formData) {
      setFormStabilized(false);

      const stabilizeTimer = setTimeout(() => {
        setFormStabilized(true);
      }, 2000);

      return () => clearTimeout(stabilizeTimer);
    }
  }, [open, results?.formData]);

  useEffect(() => {
    if (!open) {
      setFormStabilized(false);
      ignoreAutoFieldsRef.current.clear();
    }
  }, [open]);

  // PART II 20. MANAGEMENT FEE
  const { mutate: calculateRdBudgetManagementFee, data: managementFeeData } =
    useMutation(
      async (rentForecastId) => {
        try {
          const body = JSON.stringify({
            rentForecastId: rentForecastId,
          });
          const result = await post(
            '/rd-budget-form/calculate-management-fee',
            body,
          );

          const variables = result?.equation?.variables;
          const formattedEquation = result?.formattedEquation;
          let updatedEquation = formattedEquation;

          if (variables) {
            const {
              propertyUnitsCount,
              months,
              monthlyStateFee,
              smallPropertyFee,
              ...fees
            } = variables;
            const groupedParts = [
              {
                value: monthlyStateFee,
                displayLabel: 'Monthly State Fee',
                formatted: formatCurrency(monthlyStateFee),
              },
              ...[
                ...getManagementFees(managementFees),
                ...getAffordableFees(fees),
              ].map(([program, value]) => ({
                value,
                displayLabel: `${program} Fee`,
                formatted: formatCurrency(value),
              })),
              {
                value: smallPropertyFee,
                displayLabel: 'Small Property Fee',
                formatted: formatCurrency(smallPropertyFee),
              },
            ].filter((d) => d.value);

            if (groupedParts.length > 0) {
              updatedEquation = `(${groupedParts
                .map((p) => `${p.formatted} ${p.displayLabel}`)
                .join(
                  ' + ',
                )}) * ${propertyUnitsCount} Units * ${months} Months`;

              const groupedFees = groupedParts.map((p) => p.value).join(' + ');
              const textGrouped =
                groupedParts.length > 1 ? `(${groupedFees})` : groupedFees;

              useFormReturn.setValue(
                '1-2-1-comments',
                `${textGrouped} X ${propertyUnitsCount} X ${months}`,
              );
            }
          }

          return {
            ...result,
            updatedEquation: updatedEquation ?? '',
          };
        } catch (error) {
          return {
            updatedEquation: '',
            annualFee: null,
            error,
          };
        }
      },
      {
        networkMode: 'always',
      },
    );
  const rdBudgetCompliance = useRdBudgetCompliance(
    selectedProperty,
    units,
    useFormReturn,
    allowFullEdit,
  );
  React.useEffect(() => {
    if (rentForecastId) {
      calculateRdBudgetManagementFee(rentForecastId);
    }
  }, [rentForecastId, calculateRdBudgetManagementFee]);
  React.useEffect(() => {
    if (managementFeeData) {
      setValue('1-2-1-proposedBudget', managementFeeData?.annualFee);
    }
  }, [managementFeeData, setValue]);

  React.useEffect(() => {
    const hasUserChanges =
      userInteractionHappened && Object.keys(dirtyFields).length > 0;
    setGenerateDisabled(
      rdBudgetCompliance.some((section) =>
        section.items.some((item) => item.state === COMPLIANCE_STATES.fail),
      ) || hasUserChanges,
    );
  }, [rdBudgetCompliance, dirtyFields, userInteractionHappened]);

  React.useEffect(() => {
    setComplianceStatus(resolveWorstComplianceStatus(rdBudgetCompliance));
  }, [rdBudgetCompliance]);

  const statusCards = useMemo(
    () => [
      {
        label: 'Cash Flow Stmt.',
        tooltip: 'Part I - Cash Flow Statement',
        part: 'I',
        width: 1200,
        component: (
          <Part1
            useFormReturn={useFormReturn}
            intacctDifferences={intacctDifferences}
            isApprovedForecast={isApprovedForecast}
            actualDateRange={rentForecast?.parameters?.actualRange}
            rentForecast={rentForecast}
            enableAutoCalculate={!isWizardCreated}
            budgetType={budgetType}
          />
        ),
      },
      {
        label: 'Op. & Maint.',
        tooltip: 'Part II - Operating & Maintenance',
        part: 'II',
        width: 1200,
        component: (
          <Part2
            year={rentForecastYear}
            useFormReturn={useFormReturn}
            intacctDifferences={intacctDifferences}
            totalUnits={totalUnits}
            enableAutoCalculate={!isWizardCreated}
            actualDateRange={rentForecast?.parameters?.actualRange}
            rentForecast={rentForecast}
            budgetType={budgetType}
            managementFeeData={managementFeeData}
          />
        ),
      },
      {
        label: 'Acct. Bdgt./Status',
        tooltip: 'Part III - Accounting Budget/Status',
        part: 'III',
        width: 1200,
        component: (
          <Part3
            year={rentForecastYear}
            useFormReturn={useFormReturn}
            intacctDifferences={intacctDifferences}
            reserveAutoFilled={reserveAutoFilled}
            enableAutoCalculate={!isWizardCreated}
            actualDateRange={rentForecast?.parameters?.actualRange}
            rentForecast={rentForecast}
            budgetType={budgetType}
          />
        ),
      },
      {
        label: 'Annu. Cap. Bdgt.',
        tooltip: 'Part V - Annual Capital Budget',
        part: 'V',
        width: 1400,
        component: (
          <Part5
            useFormReturn={useFormReturn}
            intacctDifferences={intacctDifferences}
            budgetType={budgetType}
            rentForecast={rentForecast}
          />
        ),
      },
      ...(isWizardCreated
        ? []
        : [
            {
              label: 'Review',
              tooltip: 'Review',
              displayStatus: true,
              component: (
                <Review
                  control={control}
                  tableState={tableData}
                  setTableState={setTableData}
                  useFormReturn={useFormReturn}
                  complianceItems={rdBudgetCompliance}
                  isLoading={isTabRefreshing}
                />
              ),
            },
          ]),
      {
        label: 'Comments',
        tooltip: 'Comments',
        component: (
          <Comments
            control={control}
            setTableState={setTableData}
            useFormReturn={useFormReturn}
            fpRows={fpRows}
            rentForecast={rentForecast}
            changeType={changeType}
            vacancyRateActual={vacancyRateActual}
            isLoading={isTabRefreshing}
            disableInput={!hasEditPermission || isApprovedForecast}
          />
        ),
      },
    ],
    [
      control,
      tableData,
      setTableData,
      rentForecastYear,
      useFormReturn,
      managementFeeData,
      intacctDifferences,
      reserveAutoFilled,
      rdBudgetCompliance,
      fpRows,
      isTabRefreshing,
      isApprovedForecast,
      rentForecast,
    ],
  );

  const ActivePage = useMemo(() => {
    return statusCards[tableData?.page || 0].component;
  }, [tableData.page, statusCards]);

  const { mutate: syncIntacctBudgets, isLoading: syncIntacctBudgetsLoading } =
    useMutation(
      async () => {
        const body = JSON.stringify({
          rentForecastId,
          options: {
            accountGroupConcurrency: 5,
            accountBalanceConcurrency: 30,
            budgetItemsChunkConcurrency: 22,
            glEntryChunkConcurrency: 30,
          },
        });
        return await post('/sync-intacct-budgets', body);
      },
      {
        onSuccess: (result) => {
          const intacctFormValues = getIntacctFormValues({ ...result });
          intacctValues.current = intacctFormValues;
          setIntacctDifferences({});
          useFormReturn.reset(
            { ...getValues(), ...intacctFormValues },
            { keepDefaultValues: true },
          );
          queueMicrotask(() => {
            recalculate();
          });
          toastr.success(
            intl.formatMessage(messages.success),
            intl.formatMessage(messages.intacctSuccess),
          );
          closeIntacctModal();
        },
        onError: (error) => {
          console.error(error);

          const errorMessage = error?.message || error?.toString() || '';
          const isNoBudgetDataError = errorMessage.includes(
            'No budget data is available',
          );

          toastr.error(
            'Error',
            isNoBudgetDataError
              ? intl.formatMessage(messages.noBudgetDataAvailable)
              : intl.formatMessage(messages.intacctError),
            {
              timeOut: 0,
              showCloseButton: true,
            },
          );
        },
      },
    );

  const onSubmit = useCallback(
    (shouldCloseModal = false) =>
      (formData) => {
        if (
          formData['0-0-7-comments'] === '' ||
          formData['0-0-7-comments'] === undefined
        ) {
          formData['0-0-7-comments'] = null;
        }
        const rdBudgetFormService = new RdBudgetFormService();
        const rdBudgetData = {
          rentForecastId,
          formData,
        };

        setRequestActive(true);

        rdBudgetFormService
          .upsertRdBudgetForm(rdBudgetData)
          .then((data) => {
            const updatedAt = new Date(data?.updatedAt);
            setLastSaved(updatedAt.toLocaleString());

            setJustSaved(true);
            setAllUserInteraction(false);

            reset(formData, {
              keepValues: true,
            });

            setTimeout(() => {
              setJustSaved(false);
            }, 2000);

            if (shouldCloseModal) {
              toastr.success(
                intl.formatMessage(messages.success),
                intl.formatMessage(messages.budgetSaved),
              );
              closeModal();
            } else {
              toastr.success(
                intl.formatMessage(messages.success),
                intl.formatMessage(messages.budgetProgressSaved),
              );
            }

            setRequestActive(false);
          })
          .catch((error) => {
            toastr.error(
              intl.formatMessage(messages.error),
              intl.formatMessage(messages.saveRDError),
              { timeOut: 0, showCloseButton: true },
            );
            setJustSaved(false);
            setRequestActive(false);
          });
      },
    [reset, rentForecastId, intl, closeModal],
  );

  const Title = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Typography mr={2}>{forecastName}</Typography>
      {!isWizardCreated && (
        <>
          <Button
            variant="primarySubtle"
            onClick={openIntacctModal}
            startIcon={<RefreshIcon />}
            disabled={
              !hasEditPermission || rentForecast?.approvedFrom || isFetching
            }
          >
            <FormattedMessage {...messages.intacctHeader} />
          </Button>
          <IntacctModal
            open={intacctOpen}
            closeModal={closeIntacctModal}
            onSubmit={syncIntacctBudgets}
            isSubmitting={syncIntacctBudgetsLoading}
          />
        </>
      )}
    </Box>
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    if (results?.formData) {
      const clonedFormData = _.omit(_.cloneDeep(results.formData) || {}, [
        // These fields are calculated, ignore so they are not overwritten
        '1-2-1-comments',
        // Build Budget records keep the Rental Income and Vacancy the user saved (FES-1813)
        ...(isWizardCreated
          ? []
          : ['0-0-7-proposedBudget', '0-0-0-proposedBudget']),
      ]);

      if (results?.external?.intacct) {
        const values = getIntacctFormValues(results.external.intacct);
        intacctValues.current = values;

        triggerFullIntacctDifference(clonedFormData, values);
      }

      savedVacancyInputsRef.current =
        isWizardCreated && !_.isNil(results.formData['0-0-7-proposedBudget'])
          ? {
              rentalIncome: results.formData['0-0-0-proposedBudget'],
              rate: results.formData['0-0-7-comments'],
            }
          : null;

      useFormReturn.reset(clonedFormData, {
        keepDefaultValues: true,
      });
    }

    if (
      !isWizardCreated ||
      _.isNil(results?.formData?.['0-0-0-proposedBudget'])
    ) {
      setValue('0-0-0-proposedBudget', newbasicRentPotentialIncome * 12); // Annualize the monthly income
    }

    const part3ReserveFieldName = '2-bottom-2-Reserve Account Required Balance';
    const currentFormValue = getValues(part3ReserveFieldName);
    if (
      (currentFormValue === null || currentFormValue === undefined) &&
      reserveAccountRequiredBalance !== null &&
      reserveAccountRequiredBalance !== undefined
    ) {
      setValue(part3ReserveFieldName, reserveAccountRequiredBalance, {
        shouldDirty: false,
        shouldValidate: false,
      });
      setReserveAutoFilled(true);
    }

    recalculate(true);
  }, [
    results?.formData,
    results?.external?.intacct,
    reserveAccountRequiredBalance,
    open,
    isWizardCreated,
  ]);

  useEffect(() => {
    const part3ReserveFieldName = '2-bottom-2-Reserve Account Required Balance';
    const valueFromForm = parseFloat(getValues(part3ReserveFieldName));
    const valueFromProperty = parseFloat(reserveAccountRequiredBalance);

    if (valueFromForm.toFixed(2) !== valueFromProperty.toFixed(2)) {
      setReserveAutoFilled(false);
    } else {
      setReserveAutoFilled(true);
    }
  }, [getValues, reserveAccountRequiredBalance]);

  // PART 0 - LESS (VACANCY) value
  useEffect(() => {
    if (results) {
      let commentValue =
        rentForecast?.vacancyRateProposed ??
        rentForecast?.vacancyRate ??
        vacancyRateActual;
      if (
        (commentValue === null || commentValue === undefined) &&
        manageRentForecastData?.footerRow?.newbasicRentPotentialIncome?.value
      ) {
        commentValue =
          manageRentForecastData.footerRow.newbasicRentPotentialIncome.value;
      }
      if (commentValue !== null && commentValue !== undefined) {
        let numericCommentValue =
          typeof commentValue === 'string'
            ? parseFloat(commentValue)
            : commentValue;

        if (!isNaN(numericCommentValue)) {
          if (numericCommentValue === 0) {
            commentValue = '0%';
          } else {
            commentValue = `${numericCommentValue}%`;
          }
        } else {
          if (!commentValue.toString().endsWith('%')) {
            commentValue = `${commentValue}%`;
          }
        }

        setValue('0-0-7-comments', commentValue);
      }
    }
  }, [
    rentForecast?.vacancyRate,
    vacancyRateActual,
    manageRentForecastData,
    results,
    setValue,
  ]);

  // PART I - Calculation of Proposed Budget
  useEffect(() => {
    return subscribe({
      name: ['0-0-0-proposedBudget', '0-0-7-comments'],
      formState: { values: true },
      callback: () => {
        // This also runs when the budget loads, so Build Budget records leave
        // Vacancy to calculateVacancy, which keeps the saved amount
        if (isWizardCreated) {
          return;
        }

        const baseValueRaw = getValues('0-0-0-proposedBudget');
        const commentRaw = getValues('0-0-7-comments');
        const baseValue = parseFloat(baseValueRaw);

        if (isNaN(baseValue) || baseValue <= 0) {
          return;
        }

        let percentage = 0;

        if (commentRaw) {
          if (typeof commentRaw === 'string') {
            const cleanComment = commentRaw.replace('%', '').trim();
            percentage = parseFloat(cleanComment);
          } else if (typeof commentRaw === 'number') {
            percentage = commentRaw;
          }
        }

        if (isNaN(percentage)) {
          const fallbackRate = rentForecast?.vacancyRate ?? vacancyRateActual;
          if (fallbackRate) {
            percentage = parseFloat(fallbackRate.toString().replace('%', ''));
          }
        }
        const calculatedValue =
          Math.round(baseValue * (percentage / 100) * 100) / 100;
        const currentProposed =
          parseFloat(getValues('0-0-7-proposedBudget')) || 0;
        if (Math.abs(calculatedValue - currentProposed) > 0.01) {
          setValue('0-0-7-proposedBudget', calculatedValue, {
            shouldDirty: true,
            shouldValidate: false,
          });
        }
      },
    });
  }, [rentForecast?.vacancyRate, vacancyRateActual, isWizardCreated]);

  // PART II - Refetch data when modal opens
  useEffect(() => {
    if (!(open && rentForecastId)) {
      return;
    }

    (async () => {
      await originalRefetch();
      calculateRdBudgetManagementFee(rentForecastId);

      if (isWizardCreated) {
        return;
      }

      const baseValue = parseFloat(getValues('0-0-0-proposedBudget'));
      const comments = getValues('0-0-7-comments');

      if (!baseValue || baseValue <= 0) {
        return;
      }

      if (!comments) {
        return;
      }

      let percentage = 0;
      if (typeof comments === 'string') {
        percentage = parseFloat(comments.replace('%', '').trim());
      } else if (typeof comments === 'number') {
        percentage = comments;
      }

      if (isNaN(percentage)) {
        return;
      }

      const result = Math.round(baseValue * (percentage / 100) * 100) / 100;
      const currentProposed =
        parseFloat(getValues('0-0-7-proposedBudget')) || 0;

      if (Math.abs(result - currentProposed) > 0.01) {
        setValue('0-0-7-proposedBudget', result, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    })();
  }, [open, isWizardCreated]);

  useEffect(() => {
    if (open && results?.formData && !isWizardCreated) {
      const timeoutId = setTimeout(() => {
        const baseValue = parseFloat(getValues('0-0-0-proposedBudget'));
        const comments = getValues('0-0-7-comments');
        if (baseValue && baseValue > 0 && comments) {
          let percentage = 0;
          if (typeof comments === 'string') {
            percentage = parseFloat(comments.replace('%', '').trim());
          } else if (typeof comments === 'number') {
            percentage = comments;
          }

          if (!isNaN(percentage)) {
            const result =
              Math.round(baseValue * (percentage / 100) * 100) / 100;

            setValue('0-0-7-proposedBudget', result, {
              shouldDirty: false,
              shouldValidate: false,
            });
          }
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [open, results?.formData, setValue, useFormReturn, isWizardCreated]);

  const handleDiscardClick = () => {
    Object.keys(dirtyFields).forEach((key) => {
      resetField(key);
    });
    closeConfirmModal();
    closeModal();
  };

  const ConfirmModalProps = {
    title: intl.formatMessage(messages.confirmModalTitle),
    open: openConfirm,
    actionsProps: [
      {
        children: intl.formatMessage(messages.cancel),
        onClick: closeConfirmModal,
      },
      {
        children: intl.formatMessage(messages.confirm),
        onClick: handleDiscardClick,
      },
    ],
  };

  // Approved budgets should not change
  const disableSaves =
    !hasEditPermission ||
    rentForecast?.approvedFrom ||
    isFetching ||
    isCalculating ||
    syncIntacctBudgetsLoading ||
    Object.keys(errors).length > 0;
  const disableGeneration =
    !hasEditPermission ||
    (rentForecast?.approvedFrom
      ? false
      : isFetching ||
        isCalculating ||
        generateDisabled ||
        Object.keys(errors).length > 0);

  return (
    <>
      <Modal
        disableScrollLock={true}
        open={open}
        onClose={handleCloseWithoutSave}
        title={Title}
        actions={
          <>
            {lastSaved && (
              <Typography
                variant="bodySmall"
                color="text.secondary"
                sx={{ marginRight: 'auto' }}
              >
                <FormattedMessage {...messages.lastSaved} />: {lastSaved}
              </Typography>
            )}

            <SubmitButton
              isSubmitting={isRequestActive}
              variant="text"
              onClick={handleCloseWithoutSave}
              disabled={isFetching || isCalculating}
            >
              <FormattedMessage {...messages.cancel} />
            </SubmitButton>
            <SubmitButton
              isSubmitting={isRequestActive}
              variant="primarySubtle"
              onClick={
                rentForecast?.parameters?.budgetType
                  ? onCreate
                  : openBudgetTypeModal
              }
              disabled={disableGeneration}
            >
              <FormattedMessage {...messages.create} />
            </SubmitButton>
            <SubmitButton
              isSubmitting={isRequestActive}
              variant="info"
              onClick={handleSubmit(onSubmit())}
              disabled={disableSaves}
            >
              <FormattedMessage {...messages.saveProgress} />
            </SubmitButton>
            <SubmitButton
              isSubmitting={isRequestActive}
              variant="primary"
              onClick={handleSubmit(onSubmit(true))}
              disabled={disableSaves}
            >
              <FormattedMessage {...messages.saveAndClose} />
            </SubmitButton>
          </>
        }
        ModalProps={{
          PaperProps: {
            sx: {
              overflow: 'hidden',
              width: '100%',
              maxWidth: {
                xs: '100dvw',
                sm: 'calc(100dvw - 32px)',
                lg: 'xl',
              },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        <AsyncBox
          loading={isFetching}
          sx={{
            height: '100%',
            display: 'flex',
            width: '100%',
            overflow: 'hidden',
          }}
          ChildrenContainerProps={{
            sx: {
              width: '100%',
            },
          }}
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                flex: '0 0 auto',
                display: 'flex',
                paddingBottom: '16px',
                overflowX: 'auto',
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                zIndex: 999,
                willChange: 'transform',
                transform: 'translateZ(0)',
                width: '100%',
              }}
            >
              {statusCards.map((card, index) => (
                <StatusCard
                  key={index}
                  selected={tableData.page === index}
                  onClick={() => handleStatusCardClick(index)}
                  displayStatus={card.displayStatus}
                  complianceStatus={complianceStatus}
                  {...card}
                />
              ))}
            </Box>
            <Box
              sx={{
                flex: '1 1 auto',
                position: 'relative',
                transform: 'translateZ(0)',
                width: '100%',
                minHeight: '400px',
              }}
              ref={(el) => {
                if (el) {
                  el.scrollTop = el.scrollTop;
                  if (!el.getAttribute('tabindex')) {
                    el.setAttribute('tabindex', '-1');
                  }
                }
              }}
            >
              <Box
                display="flex"
                sx={{
                  width: '100%',
                  height: '100%',
                  overflowY: 'auto',
                }}
              >
                {ActivePage}
              </Box>
            </Box>
          </form>
        </AsyncBox>
      </Modal>
      <ModalConfirm {...ConfirmModalProps} />
      {budgetTypeModalNode}
    </>
  );
};

export default RDBudgetModal;
